import { Dayjs } from 'dayjs'
import { createSelector } from 'reselect'

import { add, DATE_TEMPLATES, day, diff, format, getDaysBetween, getToday, isAfter, isBefore } from '~/astra'
import { StoreState, useStore } from '~/store/store'

import { Include, PlanningPeriod, selectPlanningPeriods, usePlanningPeriods } from '../'

export const PLANNING_PERIOD_WITH_PPG_INCLUDE = {
    prioritizedPatientGroups: { ageGroup: true, surgeryTypeGroup: true },
} as const

export type PlanningPeriodWithPrioritizedPatientGroups = Include<PlanningPeriod, typeof PLANNING_PERIOD_WITH_PPG_INCLUDE>

export type PopulatedPrioritizedPatientGroup = PlanningPeriodWithPrioritizedPatientGroups['prioritizedPatientGroups'][number]

function unavailableDatesFromPeriods(periods: PlanningPeriod[]): Date[] {
    return periods.flatMap(period => getDaysBetween(day(period.start_date), day(period.end_date)).map(date => date.toDate()))
}

/** Storm-native equivalent of `selectUnavailableDates().all()`. */
export function useAllUnavailableDates(): Date[] {
    const periods = usePlanningPeriods()
    return unavailableDatesFromPeriods(periods)
}

/** Storm-native equivalent of `selectUnavailableDates().excludeById(id)`. */
export function useUnavailableDatesExcluding(excludePeriodId: number): Date[] {
    const periods = usePlanningPeriods({ where: { NOT: { id: excludePeriodId } } })
    return unavailableDatesFromPeriods(periods)
}

export type ProgressStatus = 'completed' | 'current' | 'future'

export function getProgressStatus(planningPeriod: PlanningPeriod): ProgressStatus {
    const today = getToday()

    if (isBefore(day(planningPeriod.end_date), today)) {
        return 'completed'
    }

    if (isAfter(day(planningPeriod.start_date), today)) {
        return 'future'
    }

    return 'current'
}

export function useSortedPlanningPeriods(): PlanningPeriod[] {
    return usePlanningPeriods({ orderBy: [['start_date', 'desc']] })
}

export function useCurrentPlanningPeriod(): PlanningPeriod | undefined {
    const todayKey = format(getToday(), DATE_TEMPLATES.iso)
    return usePlanningPeriods({
        where: { start_date: { lte: todayKey }, end_date: { gte: todayKey } },
        orderBy: [['start_date', 'desc']],
    }).at(0)
}

export function useCompletedPlanningPeriods(): PlanningPeriod[] {
    const allPeriods = usePlanningPeriods({ orderBy: [['start_date', 'asc']] })
    return allPeriods.filter(period => getProgressStatus(period) === 'completed')
}

/** Matches legacy `selectPlanningPeriods().future()` ordering (newest start_date first). */
export function useFuturePlanningPeriods(): PlanningPeriod[] {
    return useSortedPlanningPeriods().filter(period => getProgressStatus(period) === 'future')
}

/** Matches legacy `selectPlanningPeriods().completed()` ordering (newest start_date first). */
export function useCompletedPlanningPeriodsByStartDateDesc(): PlanningPeriod[] {
    return useSortedPlanningPeriods().filter(period => getProgressStatus(period) === 'completed')
}

/** Mirrors legacy `selectPlanningPeriods().byDate` — first period in slice order wins per date. */
function buildPlanningPeriodDateCache(periods: readonly PlanningPeriod[]): ReadonlyMap<string, PlanningPeriod> {
    const dateCache = new Map<string, PlanningPeriod>()
    for (const period of periods) {
        const start = day(period.start_date)
        const end = day(period.end_date)
        const daysInRange = diff(end, start, 'day') + 1

        for (let i = 0; i < daysInRange; i++) {
            const date = add(start, i, 'day')
            const dateKey = format(date, DATE_TEMPLATES.iso)
            if (!dateCache.has(dateKey)) {
                dateCache.set(dateKey, period)
            }
        }
    }
    return dateCache
}

const selectPlanningPeriodDateCache = createSelector((state: StoreState) => selectPlanningPeriods(state), buildPlanningPeriodDateCache)

/** For event handlers and filter callbacks — mirrors `usePlanningPeriodByDate`. */
export function getPlanningPeriodByDate(date: Dayjs): PlanningPeriod | undefined {
    const dateKey = format(date, DATE_TEMPLATES.iso)
    return selectPlanningPeriodDateCache(useStore.getState()).get(dateKey)
}

export function usePlanningPeriodByDate(date: Dayjs): PlanningPeriod | undefined {
    const dateKey = format(date, DATE_TEMPLATES.iso)
    const dateCache = useStore(selectPlanningPeriodDateCache)
    return dateCache.get(dateKey)
}

export function isPublishedPlanningPeriod(planningPeriod: PlanningPeriod | undefined) {
    return Boolean(planningPeriod?.is_published)
}
